import React, { useState, useEffect, useMemo, useCallback } from "react";
import adminService from "../../services/adminService";
import SimpleAdminItemForm from "./SimpleAdminItemForm";
import AdminDetailsModal from "./AdminDetailsModal";

// ✅ nouveaux modals métier HÔTEL
import HotelReservationAdminModal from "./HotelReservationAdminModal";
import AssignHotelModal from "./AssignHotelModal";

// ✅ nouveaux modals CIRCUITS
import CircuitReservationAdminModal from "./CircuitReservationAdminModal";

// ✅ nouveaux modals TRANSPORT
import TransportReservationAdminModal from "./TransportReservationAdminModal";
import AssignTransportModal from "./AssignTransportModal";

// ✅ NOUVEAU : modals VOITURE
import VoitureReservationAdminModal from "./VoitureReservationAdminModal";
import AssignVoitureModal from "./AssignVoitureModal";

// ✅ NOUVEAU : modals VISITE GUIDÉE
import VisiteReservationAdminModal from "./VisiteReservationAdminModal";
import AssignVisiteModal from "./AssignVisiteModal";

/* ---------------------------------------------
   ✅ Helper: lire un champ "dotted" (ex: client.email)
---------------------------------------------- */
const getByPath = (obj, path) => {
  if (!obj || !path) return undefined;
  return path.split(".").reduce((acc, key) => (acc ? acc[key] : undefined), obj);
};

// ✅ Base URL backend (dev/prod)
const PROD_API_BASE_URL = "https://nouzhaback-theta.vercel.app";
const LOCAL_API_BASE_URL = "http://localhost:5000";
const IS_LOCALHOST = typeof window !== "undefined" && ["localhost", "127.0.0.1"].includes(window.location.hostname);
const API_BASE_URL =
  (IS_LOCALHOST ? LOCAL_API_BASE_URL : null) ||
  process.env.REACT_APP_API_BASE_URL ||
  process.env.VITE_API_BASE_URL ||
  PROD_API_BASE_URL;

// ✅ Helper auth header (compatible avec plusieurs noms de token)
const getAuthHeaders = () => {
  const token =
    localStorage.getItem("token") ||
    localStorage.getItem("accessToken") ||
    localStorage.getItem("authToken") ||
    localStorage.getItem("jwt");

  return token ? { Authorization: `Bearer ${token}` } : {};
};

const AdminModelManager = ({ modelName }) => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [showForm, setShowForm] = useState(false);
  const [editingItem, setEditingItem] = useState(null);

  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);

  const [searchTerm, setSearchTerm] = useState("");
  const [schema, setSchema] = useState({});

  // ✅ Mode d'affichage : cartes (défaut) | tableau | compact — persisté
  const [viewMode, setViewModeState] = useState(() => {
    try { return localStorage.getItem("adminViewMode") || "cards"; } catch { return "cards"; }
  });
  const setViewMode = (v) => {
    setViewModeState(v);
    try { localStorage.setItem("adminViewMode", v); } catch (_) {}
  };

  const [showDetails, setShowDetails] = useState(false);
  const [detailItem, setDetailItem] = useState(null);

  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [itemToDelete, setItemToDelete] = useState(null);

  // ✅ modal assignation hôtel
  const [showAssignHotel, setShowAssignHotel] = useState(false);
  const [assignTarget, setAssignTarget] = useState(null);

  // ✅ modal assignation transport
  const [showAssignTransport, setShowAssignTransport] = useState(false);
  const [assignTransportTarget, setAssignTransportTarget] = useState(null);

  // ✅ NOUVEAU : modal assignation voiture
  const [showAssignVoiture, setShowAssignVoiture] = useState(false);
  const [assignVoitureTarget, setAssignVoitureTarget] = useState(null);

  // ✅ NOUVEAU : modal assignation visite
  const [showAssignVisite, setShowAssignVisite] = useState(false);
  const [assignVisiteTarget, setAssignVisiteTarget] = useState(null);

  const pageSize = 10;

  // ✅ détecter “réservation hôtels”
  const isHotelReservationModel = useMemo(() => {
    const m = (modelName || "").toLowerCase();
    return [
      "reservationhotels",
      "reservationhotel",
      "hotelreservations",
      "hotelreservation",
      "reservationshotels",
      "reservation_hotel",
      "reservations_hotel",
    ].includes(m);
  }, [modelName]);

  // ✅ détecter “réservations circuits”
  const isCircuitReservationModel = useMemo(() => {
    const m = (modelName || "").toLowerCase();
    return [
      "reservationcircuits",
      "reservationcircuit",
      "circuitreservations",
      "circuitreservation",
      "reservationscircuits",
      "reservation_circuit",
      "reservations_circuit",
    ].includes(m);
  }, [modelName]);

  // ✅ détecter “réservations transports”
  const isTransportReservationModel = useMemo(() => {
    const m = (modelName || "").toLowerCase();
    return [
      "reservationtransports",
      "reservationtransport",
      "transportreservations",
      "transportreservation",
      "reservationstransports",
      "reservation_transport",
      "reservations_transport",
    ].includes(m);
  }, [modelName]);

  // ✅ NOUVEAU : détecter “réservations voitures”
  const isVoitureReservationModel = useMemo(() => {
    const m = (modelName || "").toLowerCase();
    return [
      "reservationvoitures",
      "reservationvoiture",
      "voiturereservations",
      "voiturereservation",
      "reservationsvoitures",
      "reservation_voiture",
      "reservations_voiture",
      "reservationvoiturelocations",
      "reservationvoiturelocation",
      "reservationvoiturelocation",
    ].includes(m);
  }, [modelName]);

  // ✅ NOUVEAU : détecter “réservations visites”
  const isVisiteReservationModel = useMemo(() => {
    const m = (modelName || "").toLowerCase();
    return [
      "reservationvisites",
      "reservationvisite",
      "visitereservations",
      "visitereservation",
      "reservationsvisites",
      "reservation_visite",
      "reservations_visite",
      "reservationvisiteguidees",
      "reservationvisiteguidee",
      "reservationvisiteguidee",
    ].includes(m);
  }, [modelName]);

  // ✅ NOUVEAU : détecter “commentaires / testimonials”
  const isCommentaireModel = useMemo(() => {
    const m = (modelName || "").toLowerCase();
    return [
      "commentaires",
      "commentaire",
      "testimonials",
      "testimonial",
      "temoignages",
      "temoignage",
    ].includes(m);
  }, [modelName]);

  // ✅ fallback schema pour commentaires si ton /schema ne le renvoie pas
  const commentaireSchemaFallback = useMemo(
    () => ({
      stars: "Number",
      comment: "String",
      name: "String",
      country: "String",
      city: "String",
      status: "String",
      // pour affichage image on expose url (dotted path)
      "profilePhoto.url": "String",
      createdAt: "Date",
      updatedAt: "Date",
    }),
    []
  );

  // -----------------------------
  // ✅ API Commentaires (spécifique)
  // -----------------------------
  const fetchCommentairesAdminAll = useCallback(async () => {
    // On récupère active + inactive puis on merge
    const [a, b] = await Promise.all([
      fetch(`${API_BASE_URL}/api/commentaires?status=active&limit=200`),
      fetch(`${API_BASE_URL}/api/commentaires?status=inactive&limit=200`),
    ]);

    if (!a.ok) throw new Error("Erreur API commentaires (active)");
    if (!b.ok) throw new Error("Erreur API commentaires (inactive)");

    const [active, inactive] = await Promise.all([a.json(), b.json()]);
    const merged = [
      ...(Array.isArray(active) ? active : []),
      ...(Array.isArray(inactive) ? inactive : []),
    ];

    // tri récent
    merged.sort((x, y) => {
      const dx = new Date(x?.updatedAt || x?.createdAt || 0).getTime();
      const dy = new Date(y?.updatedAt || y?.createdAt || 0).getTime();
      return dy - dx;
    });

    return merged;
  }, []);

  const isFormData = (v) => typeof FormData !== "undefined" && v instanceof FormData;

const createCommentaireAdmin = useCallback(async (data) => {
  // ✅ si SimpleAdminItemForm t'envoie déjà FormData, on ne reconstruit rien
  const body = isFormData(data) ? data : (() => {
    const fd = new FormData();

    const file =
      data?.profilePhotoFile ||
      data?.profilePhoto ||
      data?.imageFile ||
      data?.image ||
      null;

    if (file instanceof File) fd.append("profilePhoto", file);
    fd.append("stars", data?.stars ?? data?.etoiles ?? 5);
    fd.append("comment", data?.comment ?? data?.review ?? "");
    fd.append("name", data?.name ?? data?.nom ?? "");
    fd.append("country", data?.country ?? data?.pays ?? "");
    fd.append("city", data?.city ?? data?.ville ?? "");
    fd.append("status", data?.status ?? "inactive");

    return fd;
  })();

  const res = await fetch(`${API_BASE_URL}/api/commentaires/admin`, {
    method: "POST",
    headers: { ...getAuthHeaders() }, // ✅ pas de Content-Type
    body,
  });

  if (!res.ok) {
    const text = await res.text().catch(() => "");
    throw new Error(text || "Erreur création commentaire");
  }
  return res.json();
}, []);


  const updateCommentaireAdmin = useCallback(async (id, data) => {
  const body = isFormData(data) ? data : (() => {
    const fd = new FormData();

    const file =
      data?.profilePhotoFile ||
      data?.profilePhoto ||
      data?.imageFile ||
      data?.image ||
      null;

    if (file instanceof File) fd.append("profilePhoto", file);

    if (data?.stars !== undefined) fd.append("stars", data.stars);
    if (data?.comment !== undefined) fd.append("comment", data.comment);
    if (data?.name !== undefined) fd.append("name", data.name);
    if (data?.country !== undefined) fd.append("country", data.country);
    if (data?.city !== undefined) fd.append("city", data.city);
    if (data?.status !== undefined) fd.append("status", data.status);

    return fd;
  })();

  const res = await fetch(`${API_BASE_URL}/api/commentaires/admin/${id}`, {
    method: "PATCH",
    headers: { ...getAuthHeaders() },
    body,
  });

  if (!res.ok) {
    const text = await res.text().catch(() => "");
    throw new Error(text || "Erreur modification commentaire");
  }
  return res.json();
}, []);


  const deleteCommentaireAdmin = useCallback(async (id) => {
    const res = await fetch(`${API_BASE_URL}/api/commentaires/admin/${id}`, {
      method: "DELETE",
      headers: {
        ...getAuthHeaders(),
      },
    });

    if (!res.ok) {
      const text = await res.text().catch(() => "");
      throw new Error(text || "Erreur suppression commentaire");
    }
    return res.json();
  }, []);

  // -----------------------------
  // LOAD DATA
  // -----------------------------
  useEffect(() => {
    const loadData = async () => {
      try {
        setLoading(true);

        // ✅ Cas Commentaires: on gère list + schema ici
        if (isCommentaireModel) {
          const all = await fetchCommentairesAdminAll();

          // recherche client-side
          const q = (searchTerm || "").trim().toLowerCase();
          const filtered = !q
            ? all
            : all.filter((it) => {
                const hay = [
                  it?.comment,
                  it?.name,
                  it?.country,
                  it?.city,
                  it?.status,
                  String(it?.stars ?? ""),
                ]
                  .filter(Boolean)
                  .join(" ")
                  .toLowerCase();
                return hay.includes(q);
              });

          // pagination client-side
          const total = Math.max(1, Math.ceil(filtered.length / pageSize));
          setTotalPages(total);

          const start = (currentPage - 1) * pageSize;
          const paged = filtered.slice(start, start + pageSize);

          setItems(paged);
          setSchema(commentaireSchemaFallback);
          setError(null);
          return;
        }

        // ✅ Autres modèles (adminService normal)
        const [itemsResponse, schemaResponse] = await Promise.all([
          adminService.getAllItems(modelName, {
            page: currentPage,
            limit: pageSize,
            search: searchTerm,
          }),
          adminService.getModelSchema(modelName),
        ]);

        setItems(itemsResponse.data.items);
        setTotalPages(itemsResponse.data.pagination.total);
        setSchema(schemaResponse.data);
        setError(null);
      } catch (error) {
        setError("Erreur lors du chargement des données");
        console.error("Erreur:", error);
      } finally {
        setLoading(false);
      }
    };

    loadData();
  }, [
    modelName,
    currentPage,
    searchTerm,
    isCommentaireModel,
    fetchCommentairesAdminAll,
    commentaireSchemaFallback,
  ]);

  const loadItems = async () => {
    try {
      setLoading(true);

      if (isCommentaireModel) {
        const all = await fetchCommentairesAdminAll();

        const q = (searchTerm || "").trim().toLowerCase();
        const filtered = !q
          ? all
          : all.filter((it) => {
              const hay = [
                it?.comment,
                it?.name,
                it?.country,
                it?.city,
                it?.status,
                String(it?.stars ?? ""),
              ]
                .filter(Boolean)
                .join(" ")
                .toLowerCase();
              return hay.includes(q);
            });

        const total = Math.max(1, Math.ceil(filtered.length / pageSize));
        setTotalPages(total);

        const start = (currentPage - 1) * pageSize;
        setItems(filtered.slice(start, start + pageSize));
        setError(null);
        return;
      }

      const response = await adminService.getAllItems(modelName, {
        page: currentPage,
        limit: pageSize,
        search: searchTerm,
      });
      setItems(response.data.items);
      setTotalPages(response.data.pagination.total);
      setError(null);
    } catch (error) {
      setError("Erreur lors du chargement des données");
      console.error("Erreur:", error);
    } finally {
      setLoading(false);
    }
  };

  // ---- actions génériques ----
  const handleCreate = () => {
    setEditingItem(null);
    setShowForm(true);
  };

  const handleEdit = (item) => {
    setEditingItem(item);
    setShowForm(true);
  };

  const handleDetails = (item) => {
    setDetailItem(item);
    setShowDetails(true);
  };

  const handleDelete = (item) => {
    setItemToDelete(item);
    setShowDeleteModal(true);
  };

  // ✅ action assignation hôtel
  const handleAssignHotel = (item) => {
    setAssignTarget(item);
    setShowAssignHotel(true);
  };

  // ✅ action assignation transport
  const handleAssignTransport = (item) => {
    setAssignTransportTarget(item);
    setShowAssignTransport(true);
  };

  // ✅ NOUVEAU : action assignation voiture
  const handleAssignVoiture = (item) => {
    setAssignVoitureTarget(item);
    setShowAssignVoiture(true);
  };

  // ✅ NOUVEAU : action assignation visite
  const handleAssignVisite = (item) => {
    setAssignVisiteTarget(item);
    setShowAssignVisite(true);
  };

  const confirmDelete = async () => {
    if (!itemToDelete) return;

    try {
      if (isCommentaireModel) {
        await deleteCommentaireAdmin(itemToDelete._id);
      } else {
        await adminService.deleteItem(modelName, itemToDelete._id);
      }
      await loadItems();
      setShowDeleteModal(false);
      setItemToDelete(null);
    } catch (error) {
      console.error(error);
      alert("Erreur lors de la suppression");
    }
  };

  const cancelDelete = () => {
    setShowDeleteModal(false);
    setItemToDelete(null);
  };

  const handleFormSubmit = async (data) => {
    try {
      if (isCommentaireModel) {
        // ✅ Commentaires: endpoints dédiés + Cloudinary (FormData)
        if (editingItem) {
          await updateCommentaireAdmin(editingItem._id, data);
        } else {
          await createCommentaireAdmin(data);
        }
      } else {
        // ✅ autres modèles : adminService classique
        if (editingItem) {
          await adminService.updateItem(modelName, editingItem._id, data);
        } else {
          await adminService.createItem(modelName, data);
        }
      }

      setShowForm(false);
      setEditingItem(null);
      await loadItems();
    } catch (error) {
      console.error(error);
      alert("Erreur lors de la sauvegarde");
    }
  };

  // ✅ toggle status rapide pour commentaires
  const handleToggleCommentStatus = async (item) => {
    try {
      const nextStatus = item?.status === "active" ? "inactive" : "active";
      await updateCommentaireAdmin(item._id, { status: nextStatus });
      await loadItems();
    } catch (e) {
      console.error(e);
      alert("Erreur lors du changement de statut");
    }
  };

  const handleSearch = (e) => {
    setSearchTerm(e.target.value);
    setCurrentPage(1);
  };

  const getDisplayValue = (value, fieldName) => {
    if (value === null || value === undefined) return "-";
    if (typeof value === "boolean") return value ? "✅" : "❌";
    if (Array.isArray(value)) return `[${value.length} éléments]`;

    // ✅ cas "profilePhoto" objet { url, publicId }
    if (typeof value === "object" && value?.url && typeof value.url === "string") {
      const url = value.url;
      if (url.startsWith("http") || url.startsWith("data:")) {
        return (
          <div className="flex items-center space-x-2">
            <img
              src={url}
              alt="Aperçu"
              className="w-12 h-12 object-cover rounded-full border border-slate-200 shadow-sm"
              onError={(e) => {
                e.target.style.display = "none";
                if (e.target.nextSibling && e.target.nextSibling.style) {
                  e.target.nextSibling.style.display = "inline-flex";
                }
              }}
            />
            <div className="hidden items-center justify-center w-12 h-12 bg-slate-100 rounded-full border border-slate-200">
              <span className="text-slate-400 text-xs">❌</span>
            </div>
            <span className="text-xs text-slate-500 max-w-[120px] truncate">
              {url.substring(url.lastIndexOf("/") + 1)}
            </span>
          </div>
        );
      }
    }

    // ✅ si un objet est "populate", essaye d'afficher un champ lisible
    if (typeof value === "object") {
      // cas ObjectId "pur"
      if (value._id && Object.keys(value).length === 1) return String(value._id);

      const readable =
        value.titre ||
        value.nomComplet ||
        value.nom ||
        value.prenom ||
        value.username ||
        value.email ||
        value.telephone ||
        value.modele ||
        value.marque;

      return readable ? String(readable) : "Objet";
    }

    // images profil (ancien champ)
    if (fieldName === "profileImage" && value && typeof value === "string") {
      const imageUrl = value.startsWith("http")
        ? value
        : `https://nouzhaback-theta.vercel.app${value}`;
      return (
        <div className="flex items-center space-x-2">
          <img
            src={imageUrl}
            alt="Profile"
            className="w-12 h-12 object-cover rounded-full border-2 border-slate-200 shadow-sm"
            onError={(e) => {
              e.target.style.display = "none";
              if (e.target.nextSibling && e.target.nextSibling.style) {
                e.target.nextSibling.style.display = "inline-flex";
              }
            }}
          />
          <div className="hidden items-center justify-center w-12 h-12 bg-gradient-to-r from-orange-400 to-red-500 text-white rounded-full border-2 border-slate-200 text-lg font-bold">
            {value.includes("admin") ? "A" : "U"}
          </div>
          <span className="text-xs text-slate-500 max-w-[100px] truncate">
            {value.substring(value.lastIndexOf("/") + 1)}
          </span>
        </div>
      );
    }

    // champs image string
    if (
      (fieldName === "image" || fieldName.toLowerCase().includes("image")) &&
      typeof value === "string" &&
      (value.startsWith("http") || value.startsWith("data:"))
    ) {
      return (
        <div className="flex items-center space-x-2">
          <img
            src={value}
            alt="Aperçu"
            className="w-12 h-12 object-cover rounded-lg border border-slate-200 shadow-sm"
            onError={(e) => {
              e.target.style.display = "none";
              if (e.target.nextSibling && e.target.nextSibling.style) {
                e.target.nextSibling.style.display = "inline-flex";
              }
            }}
          />
          <div className="hidden items-center justify-center w-12 h-12 bg-slate-100 rounded-lg border border-slate-200">
            <span className="text-slate-400 text-xs">❌</span>
          </div>
          <span className="text-xs text-slate-500 max-w-[100px] truncate">
            {value.substring(value.lastIndexOf("/") + 1)}
          </span>
        </div>
      );
    }

    if (typeof value === "string" && value.length > 50) {
      return value.substring(0, 50) + "...";
    }
    return value.toString();
  };

  const getFieldDisplayName = (fieldName) => {
    const translationsMap = {
      _id: "ID",
      createdAt: "Créé le",
      updatedAt: "Modifié le",
      nom: "Nom",
      email: "Email",
      username: "Nom d'utilisateur",
      role: "Rôle",
      profileImage: "Photo de profil",
      titre: "Titre",
      description: "Description",
      prix: "Prix",
      etoiles: "Étoiles",
      ville: "Ville",
      adresse: "Adresse",
      telephone: "Téléphone",
      duree: "Durée",
      inclusions: "Inclusions",
      exclusions: "Exclusions",
      date_debut: "Date début",
      date_fin: "Date fin",
      nb_personnes: "Nb personnes",
      nb_etoiles: "Étoiles",
      prix_total: "Prix total",
      prixFinal: "Prix final",
      paymentUrl: "Lien paiement",
      pointDepart: "Départ",
      pointArrivee: "Arrivée",
      transportType: "Type transport",
      statut: "Statut",

      // ✅ Commentaires
      stars: "Étoiles",
      comment: "Commentaire",
      name: "Nom",
      country: "Pays",
      city: "Ville",
      status: "Statut",
      "profilePhoto.url": "Photo",

      // ✅ BONUS
      pickupLocation: "Lieu de prise",
      location: "Lieu",
      numberOfPeople: "Nb personnes",
      tourType: "Type visite",
      budgetParJour: "Budget/jour",
      budgetTotal: "Budget total",
      souhaiteReduction: "Réduction",
      acceptCgv: "CGV",
      lang: "Langue",

      "client.nomComplet": "Client - Nom complet",
      "client.email": "Client - Email",
      "client.telephone": "Client - Téléphone",
      "client.adresse": "Client - Adresse",

      visite: "Visite",
      utilisateur: "Utilisateur",
    };

    return translationsMap[fieldName] || fieldName;
  };

  if (loading && items.length === 0) {
    return (
      <div className="bg-white rounded-3xl shadow-sm border border-slate-100 p-5 lg:p-6">
        <style>{`
          .nz-skel{position:relative;overflow:hidden;background:#eef2f7}
          .nz-skel::after{content:"";position:absolute;inset:0;transform:translateX(-100%);background:linear-gradient(90deg,transparent,rgba(255,255,255,.7),transparent);animation:nz-shimmer 1.4s infinite}
          @keyframes nz-shimmer{100%{transform:translateX(100%)}}
        `}</style>
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-3">
            <div className="w-11 h-11 rounded-2xl nz-skel" />
            <div className="space-y-2">
              <div className="h-4 w-44 rounded nz-skel" />
              <div className="h-3 w-24 rounded nz-skel" />
            </div>
          </div>
          <div className="h-10 w-28 rounded-xl nz-skel" />
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4 lg:gap-5">
          {[...Array(6)].map((_, i) => (
            <div key={i} className="rounded-2xl border border-slate-100 overflow-hidden">
              <div className="h-36 nz-skel" />
              <div className="p-4 space-y-3">
                <div className="h-4 w-2/3 rounded nz-skel" />
                <div className="h-3 w-full rounded nz-skel" />
                <div className="h-3 w-5/6 rounded nz-skel" />
                <div className="flex gap-2 pt-2">
                  <div className="h-8 flex-1 rounded-lg nz-skel" />
                  <div className="h-8 flex-1 rounded-lg nz-skel" />
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  const modelDisplayName = modelName
    ? modelName.charAt(0).toUpperCase() + modelName.slice(1)
    : "Modèle";

  // ✅ pour les réservations : on masque CRUD
  const showCrudButtons =
    !isHotelReservationModel &&
    !isCircuitReservationModel &&
    !isTransportReservationModel &&
    !isVoitureReservationModel &&
    !isVisiteReservationModel;

  // ===== Champs pour l'affichage en cartes =====
  const cardFields = Object.keys(schema).filter((f) => f !== "mdp" && f !== "password");
  const imageField = cardFields.find((f) => /image|photo|logo|cover|picture|avatar/i.test(f));
  const titleField =
    cardFields.find((f) => /titre|title|nom\b|name|username|hotel|ville|reference/i.test(f)) ||
    cardFields.find((f) => f !== imageField) ||
    cardFields[0];
  const infoFields = cardFields.filter((f) => f !== imageField && f !== titleField).slice(0, 4);
  const resolveImg = (raw) =>
    !raw ? null : String(raw).startsWith("http") ? raw : `${API_BASE_URL}${raw}`;

  // ✅ Boutons d'action (réutilisés par les 3 vues)
  const renderActions = (item) => (
    <>
      <button onClick={() => handleDetails(item)} className="text-emerald-700 px-3 py-1.5 rounded-lg border border-emerald-200 bg-emerald-50 hover:bg-emerald-100 transition-all text-xs font-semibold">👁️ Détails</button>
      {isCommentaireModel && (
        <button onClick={() => handleToggleCommentStatus(item)} className={`px-3 py-1.5 rounded-lg border transition-all text-xs font-semibold ${item?.status === "active" ? "text-orange-700 border-orange-200 bg-orange-50 hover:bg-orange-100" : "text-emerald-700 border-emerald-200 bg-emerald-50 hover:bg-emerald-100"}`}>{item?.status === "active" ? "⛔ Désactiver" : "✅ Activer"}</button>
      )}
      {isTransportReservationModel && (<button onClick={() => handleAssignTransport(item)} className="text-indigo-700 px-3 py-1.5 rounded-lg border border-indigo-200 bg-indigo-50 hover:bg-indigo-100 transition-all text-xs font-semibold">🚌 Assigner</button>)}
      {isHotelReservationModel && (<button onClick={() => handleAssignHotel(item)} className="text-orange-700 px-3 py-1.5 rounded-lg border border-orange-200 bg-orange-50 hover:bg-orange-100 transition-all text-xs font-semibold">🏨 Assigner</button>)}
      {isVoitureReservationModel && (<button onClick={() => handleAssignVoiture(item)} className="text-indigo-700 px-3 py-1.5 rounded-lg border border-indigo-200 bg-indigo-50 hover:bg-indigo-100 transition-all text-xs font-semibold">🚗 Assigner</button>)}
      {isVisiteReservationModel && (<button onClick={() => handleAssignVisite(item)} className="text-indigo-700 px-3 py-1.5 rounded-lg border border-indigo-200 bg-indigo-50 hover:bg-indigo-100 transition-all text-xs font-semibold">🧭 Assigner</button>)}
      {showCrudButtons && (
        <>
          <button onClick={() => handleEdit(item)} className="text-blue-700 px-3 py-1.5 rounded-lg border border-blue-200 bg-blue-50 hover:bg-blue-100 transition-all text-xs font-semibold">✏️ Modifier</button>
          <button onClick={() => handleDelete(item)} className="text-red-700 px-3 py-1.5 rounded-lg border border-red-200 bg-red-50 hover:bg-red-100 transition-all text-xs font-semibold">🗑️ Supprimer</button>
        </>
      )}
    </>
  );

  // ✅ Switcher de vue (3 modes)
  const VIEW_OPTIONS = [
    { id: "cards", label: "Cartes", icon: "▦" },
    { id: "table", label: "Tableau", icon: "☰" },
    { id: "compact", label: "Compact", icon: "≣" },
  ];
  const ViewSwitcher = () => (
    <div className="inline-flex items-center bg-slate-100 rounded-xl p-1 gap-1">
      {VIEW_OPTIONS.map((v) => (
        <button
          key={v.id}
          onClick={() => setViewMode(v.id)}
          title={v.label}
          className={`px-3 py-1.5 rounded-lg text-sm font-semibold transition-all flex items-center gap-1.5 ${
            viewMode === v.id ? "bg-white text-orange-600 shadow-sm" : "text-slate-500 hover:text-slate-700"
          }`}
        >
          <span className="text-base leading-none">{v.icon}</span>
          <span className="hidden xl:inline">{v.label}</span>
        </button>
      ))}
    </div>
  );

  return (
    <div className="bg-white rounded-3xl shadow-sm border border-slate-100 overflow-hidden">
      {/* ===== Toolbar ===== */}
      <div className="p-5 lg:p-6 border-b border-slate-100">
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
          <div className="flex items-center gap-3">
            <span className="w-11 h-11 flex-none rounded-2xl bg-gradient-to-br from-orange-500 to-red-600 text-white flex items-center justify-center text-xl shadow-md">📊</span>
            <div>
              <h2 className="text-lg lg:text-xl font-black text-slate-800 leading-tight">
                Gestion des {modelDisplayName}
              </h2>
              <p className="text-xs text-slate-500 mt-0.5">
                <span className="font-bold text-slate-700">{items.length}</span> élément{items.length !== 1 ? "s" : ""} (page courante)
              </p>
            </div>
          </div>

          <div className="flex flex-col sm:flex-row gap-3 lg:items-center">
            <div className="hidden sm:block"><ViewSwitcher /></div>
            <div className="relative flex-1 sm:w-64">
              <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none">🔍</span>
              <input
                type="text"
                placeholder="Rechercher..."
                value={searchTerm}
                onChange={handleSearch}
                className="w-full pl-9 pr-4 py-2.5 bg-slate-50 border border-slate-200 rounded-xl focus:ring-2 focus:ring-orange-400 focus:border-transparent focus:bg-white transition-all text-sm"
              />
            </div>
            {showCrudButtons && (
              <button
                onClick={handleCreate}
                className="px-5 py-2.5 bg-gradient-to-r from-orange-500 to-red-500 text-white rounded-xl hover:from-orange-600 hover:to-red-600 transition-all flex items-center justify-center gap-2 shadow-lg shadow-orange-200 hover:scale-[1.03] font-semibold text-sm whitespace-nowrap"
              >
                <span>➕</span><span>Ajouter</span>
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Error State */}
      {error && (
        <div className="p-4 lg:p-6 bg-red-50 border-l-4 border-red-400">
          <div className="flex items-center">
            <span className="text-red-500 mr-2">⚠️</span>
            <p className="text-red-700 text-sm">{error}</p>
          </div>
        </div>
      )}

      {/* Items List */}
      {items.length === 0 && !loading ? (
        <div className="p-10 lg:p-16 text-center">
          <div className="w-20 h-20 mx-auto rounded-3xl bg-slate-50 border border-slate-100 flex items-center justify-center text-4xl mb-4">📭</div>
          <p className="text-slate-700 text-lg font-bold mb-1">Aucun élément trouvé</p>
          <p className="text-slate-400 text-sm">
            Commencez par ajouter un nouvel élément
          </p>
        </div>
      ) : (
        viewMode === "table" ? (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-slate-50/80 border-b border-slate-100">
                <tr>
                  {cardFields.slice(0, 6).map((field) => (
                    <th key={field} className="px-6 py-3.5 text-left text-[11px] font-bold text-slate-400 uppercase tracking-[0.08em]">{getFieldDisplayName(field)}</th>
                  ))}
                  <th className="px-6 py-3.5 text-right text-[11px] font-bold text-slate-400 uppercase tracking-[0.08em]">Actions</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-slate-100">
                {items.map((item) => (
                  <tr key={item._id} className="hover:bg-orange-50/40 transition-colors">
                    {cardFields.slice(0, 6).map((field) => (
                      <td key={field} className="px-6 py-4 whitespace-nowrap text-sm text-slate-700">{getDisplayValue(getByPath(item, field), field)}</td>
                    ))}
                    <td className="px-6 py-4 whitespace-nowrap text-right">
                      <div className="flex gap-2 justify-end flex-wrap">{renderActions(item)}</div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : viewMode === "compact" ? (
          <div className="divide-y divide-slate-100">
            {items.map((item) => {
              const imgSrc = imageField ? resolveImg(getByPath(item, imageField)) : null;
              return (
                <div key={item._id} className="flex items-center gap-3 px-4 lg:px-6 py-3 hover:bg-orange-50/40 transition-colors">
                  <div className="w-11 h-11 flex-none rounded-xl bg-slate-100 overflow-hidden flex items-center justify-center text-lg text-slate-300">
                    {imgSrc ? <img src={imgSrc} alt="" className="w-full h-full object-cover" onError={(e) => { e.currentTarget.style.display = "none"; }} /> : "📦"}
                  </div>
                  <div className="min-w-0 flex-1">
                    <p className="font-semibold text-slate-800 text-sm truncate">{getDisplayValue(getByPath(item, titleField), titleField) || "—"}</p>
                    <p className="text-xs text-slate-400 truncate">{infoFields.slice(0, 2).map((f) => `${getFieldDisplayName(f)}: ${getDisplayValue(getByPath(item, f), f)}`).join("  ·  ")}</p>
                  </div>
                  <div className="flex gap-1.5 flex-none flex-wrap justify-end max-w-[55%]">{renderActions(item)}</div>
                </div>
              );
            })}
          </div>
        ) : (
          <div className="p-4 lg:p-6 grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4 lg:gap-5">
            {items.map((item) => {
              const imgSrc = imageField ? resolveImg(getByPath(item, imageField)) : null;
              return (
                <div key={item._id} className="group bg-white rounded-2xl border border-slate-100 shadow-sm hover:shadow-xl hover:-translate-y-1 transition-all duration-300 overflow-hidden flex flex-col">
                  {imageField && (
                    <div className="h-36 bg-slate-100 relative overflow-hidden flex-none">
                      {imgSrc ? <img src={imgSrc} alt="" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" onError={(e) => { e.currentTarget.style.display = "none"; }} /> : <div className="w-full h-full flex items-center justify-center text-4xl text-slate-300">🏞️</div>}
                      <div className="absolute inset-x-0 bottom-0 h-12 bg-gradient-to-t from-black/30 to-transparent" />
                    </div>
                  )}
                  <div className="p-4 flex-1 flex flex-col">
                    <h3 className="font-bold text-slate-800 truncate">{getDisplayValue(getByPath(item, titleField), titleField) || "—"}</h3>
                    <div className="mt-2.5 space-y-1.5 flex-1">
                      {infoFields.map((f) => (
                        <div key={f} className="flex items-center justify-between gap-2 text-xs">
                          <span className="text-slate-400 font-semibold uppercase tracking-wide flex-none">{getFieldDisplayName(f)}</span>
                          <span className="text-slate-700 font-medium text-right truncate">{getDisplayValue(getByPath(item, f), f)}</span>
                        </div>
                      ))}
                    </div>
                    <div className="flex flex-wrap gap-2 mt-4 pt-3 border-t border-slate-100">{renderActions(item)}</div>
                  </div>
                </div>
              );
            })}
          </div>
        )
      )}

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="p-4 lg:p-6 border-t border-slate-200 bg-slate-50">
          <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center space-y-3 sm:space-y-0">
            <div className="text-center sm:text-left">
              <p className="text-sm text-slate-700">
                Page <span className="font-semibold">{currentPage}</span> sur{" "}
                <span className="font-semibold">{totalPages}</span>
              </p>
            </div>
            <div className="flex justify-center sm:justify-end space-x-2">
              <button
                onClick={() => setCurrentPage(currentPage - 1)}
                disabled={currentPage === 1}
                className="px-4 py-2 border border-slate-300 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed hover:bg-slate-100 transition-colors text-sm font-medium"
              >
                ← Précédent
              </button>
              <button
                onClick={() => setCurrentPage(currentPage + 1)}
                disabled={currentPage === totalPages}
                className="px-4 py-2 border border-slate-300 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed hover:bg-slate-100 transition-colors text-sm font-medium"
              >
                Suivant →
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Form Modal (CRUD) */}
      {showForm && showCrudButtons && (
        <SimpleAdminItemForm
          modelName={modelName}
          schema={schema}
          initialData={editingItem}
          onSubmit={handleFormSubmit}
          onCancel={() => {
            setShowForm(false);
            setEditingItem(null);
          }}
        />
      )}

      {/* Details Modal */}
      {showDetails && detailItem && (
        isHotelReservationModel ? (
          <HotelReservationAdminModal
            reservation={detailItem}
            onClose={() => setShowDetails(false)}
            onUpdated={loadItems}
          />
        ) : isCircuitReservationModel ? (
          <CircuitReservationAdminModal
            reservation={detailItem}
            onClose={() => setShowDetails(false)}
            onUpdated={loadItems}
          />
        ) : isTransportReservationModel ? (
          <TransportReservationAdminModal
            reservation={detailItem}
            onClose={() => setShowDetails(false)}
            onUpdated={loadItems}
          />
        ) : isVoitureReservationModel ? (
          <VoitureReservationAdminModal
            reservation={detailItem}
            onClose={() => setShowDetails(false)}
            onUpdated={loadItems}
          />
        ) : isVisiteReservationModel ? (
          <VisiteReservationAdminModal
            reservation={detailItem}
            onClose={() => setShowDetails(false)}
            onUpdated={loadItems}
          />
        ) : (
          <AdminDetailsModal
            item={detailItem}
            schema={schema}
            onClose={() => setShowDetails(false)}
          />
        )
      )}

      {/* Assign Hotel Modal */}
      {showAssignHotel && assignTarget && (
        <AssignHotelModal
          reservation={assignTarget}
          onClose={() => {
            setShowAssignHotel(false);
            setAssignTarget(null);
          }}
          onAssigned={async () => {
            setShowAssignHotel(false);
            setAssignTarget(null);
            await loadItems();
          }}
        />
      )}

      {/* Assign Transport Modal */}
      {showAssignTransport && assignTransportTarget && (
        <AssignTransportModal
          reservation={assignTransportTarget}
          onClose={() => {
            setShowAssignTransport(false);
            setAssignTransportTarget(null);
          }}
          onAssigned={async () => {
            setShowAssignTransport(false);
            setAssignTransportTarget(null);
            await loadItems();
          }}
        />
      )}

      {/* ✅ Assign Voiture Modal */}
      {showAssignVoiture && assignVoitureTarget && (
        <AssignVoitureModal
          reservation={assignVoitureTarget}
          onClose={() => {
            setShowAssignVoiture(false);
            setAssignVoitureTarget(null);
          }}
          onAssigned={async () => {
            setShowAssignVoiture(false);
            setAssignVoitureTarget(null);
            await loadItems();
          }}
        />
      )}

      {/* ✅ Assign Visite Modal */}
      {showAssignVisite && assignVisiteTarget && (
        <AssignVisiteModal
          reservation={assignVisiteTarget}
          onClose={() => {
            setShowAssignVisite(false);
            setAssignVisiteTarget(null);
          }}
          onAssigned={async () => {
            setShowAssignVisite(false);
            setAssignVisiteTarget(null);
            await loadItems();
          }}
        />
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteModal && itemToDelete && (
        <div className="fixed inset-0 bg-black/50 z-[70] flex items-center justify-center p-4 pt-20">
          <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full mx-4 transform transition-all">
            <div className="p-6">
              <div className="flex items-center justify-center w-16 h-16 mx-auto mb-4 bg-red-100 rounded-full">
                <svg
                  className="w-8 h-8 text-red-600"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
                  />
                </svg>
              </div>
              <h3 className="text-xl font-bold text-slate-900 text-center mb-2">
                Confirmer la suppression
              </h3>
              <p className="text-slate-600 text-center mb-6">
                Êtes-vous sûr de vouloir supprimer cet élément ? Cette action est
                irréversible.
              </p>

              {(itemToDelete.nom || itemToDelete.name) && (
                <div className="bg-slate-50 rounded-lg p-3 mb-6">
                  <p className="text-sm text-slate-600 text-center">
                    <span className="font-medium">Élément à supprimer :</span>{" "}
                    {itemToDelete.nom || itemToDelete.name}
                  </p>
                </div>
              )}

              <div className="flex space-x-3">
                <button
                  onClick={cancelDelete}
                  className="flex-1 px-4 py-3 text-sm font-semibold text-slate-700 bg-slate-100 hover:bg-slate-200 rounded-xl transition-all duration-200"
                >
                  Annuler
                </button>
                <button
                  onClick={confirmDelete}
                  className="flex-1 px-4 py-3 text-sm font-semibold text-white bg-red-600 hover:bg-red-700 rounded-xl transition-all duration-200 shadow-lg hover:shadow-xl"
                >
                  Supprimer
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminModelManager;
